/**
 * Contract Signing Component
 * First-purchase only: client must scroll through the full agreement,
 * type their name as a digital signature, and accept.
 * Records: typed name, IP, user agent, timestamp, contract version.
 */

import { useState, useRef, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../services/firebase';

const CONTRACT_VERSION = '1.0';
const CONTRACT_URL = '/terms-of-service-v1.0.html';

interface ContractSignProps {
  isOpen: boolean;
  onClose: () => void;
  onSigned: () => void;
  clientName: string;
}

export function ContractSign({ isOpen, onClose, onSigned, clientName }: ContractSignProps) {
  const [contractHtml, setContractHtml] = useState<string>('');
  const [loadError, setLoadError] = useState(false);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const [typedName, setTypedName] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    setScrolledToBottom(false);
    setTypedName('');
    setAgreed(false);
    setError(null);

    fetch(CONTRACT_URL)
      .then(r => {
        if (!r.ok) throw new Error('Failed to load');
        return r.text();
      })
      .then(setContractHtml)
      .catch(() => setLoadError(true));
  }, [isOpen]);

  if (!isOpen) return null;

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 50;
    if (atBottom) setScrolledToBottom(true);
  };

  const handleSign = async () => {
    if (!typedName.trim() || !agreed || !scrolledToBottom) return;
    setSigning(true);
    setError(null);

    try {
      const fn = httpsCallable(functions, 'signContract');
      await fn({
        typedName: typedName.trim(),
        contractVersion: CONTRACT_VERSION,
      });
      onSigned();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Signing failed');
      setSigning(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100,
    }}>
      <div style={{
        background: 'var(--dark-2)', borderRadius: '12px', padding: '24px',
        maxWidth: '700px', width: '95%', border: '1px solid var(--dark-3)',
        maxHeight: '95vh', display: 'flex', flexDirection: 'column',
      }}>
        <h2 style={{ margin: '0 0 4px', color: 'var(--white)' }}>Service Agreement</h2>
        <p style={{ color: 'var(--gray-400)', fontSize: '0.8rem', marginBottom: '12px' }}>
          Please read the full agreement below and sign to continue. This is required for your first purchase.
        </p>

        {/* Scrollable contract */}
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          style={{
            flex: 1, overflowY: 'auto', padding: '16px', marginBottom: '12px',
            background: '#fff', color: '#1a1a1a', borderRadius: '8px',
            fontSize: '0.85rem', lineHeight: '1.5', minHeight: '300px', maxHeight: '50vh',
          }}
        >
          {loadError ? (
            <p style={{ color: '#dc2626' }}>Failed to load the agreement. Please try again later.</p>
          ) : contractHtml ? (
            <div dangerouslySetInnerHTML={{ __html: contractHtml }} />
          ) : (
            <p>Loading agreement...</p>
          )}
        </div>

        {!scrolledToBottom && !loadError && (
          <p style={{ color: 'var(--gold)', fontSize: '0.8rem', textAlign: 'center', marginBottom: '8px' }}>
            Please scroll to the bottom of the agreement to continue.
          </p>
        )}

        {scrolledToBottom && (
          <>
            {/* Digital signature */}
            <div style={{ marginBottom: '12px' }}>
              <label style={{ color: 'var(--gray-300)', fontSize: '0.85rem', display: 'block', marginBottom: '6px' }}>
                Type your full legal name as your digital signature:
              </label>
              <input
                type="text"
                value={typedName}
                onChange={e => setTypedName(e.target.value)}
                placeholder={clientName || 'Your full name'}
                style={{
                  width: '100%', padding: '12px', fontSize: '1.1rem',
                  fontFamily: "'Brush Script MT', 'Segoe Script', cursive",
                  background: 'var(--dark)', border: '1px solid var(--dark-3)',
                  borderRadius: '6px', color: 'var(--white)', textAlign: 'center',
                }}
              />
            </div>

            {/* Checkbox */}
            <label style={{
              display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '12px',
              cursor: 'pointer', color: 'var(--gray-300)', fontSize: '0.85rem',
            }}>
              <input
                type="checkbox"
                checked={agreed}
                onChange={e => setAgreed(e.target.checked)}
                style={{ accentColor: 'var(--gold)', marginTop: '2px' }}
              />
              I have read and agree to the JAD Enterprises Service Agreement (v{CONTRACT_VERSION}). I understand that credits are non-refundable once used.
            </label>

            {error && <p style={{ color: '#dc2626', fontSize: '0.8rem', marginBottom: '8px' }}>{error}</p>}

            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={onClose} className="btn btn-secondary" style={{ flex: 1 }}>Cancel</button>
              <button
                onClick={handleSign}
                disabled={!typedName.trim() || !agreed || signing}
                className="btn btn-primary"
                style={{ flex: 1 }}
              >
                {signing ? 'Signing...' : 'Sign Agreement'}
              </button>
            </div>
          </>
        )}

        {!scrolledToBottom && (
          <button onClick={onClose} className="btn btn-secondary" style={{ width: '100%' }}>Cancel</button>
        )}
      </div>
    </div>
  );
}
